import type { ChunkRangeOut, SortReach } from "daedalus-core";
import { neighbourMapIds, pagesOfMap, type AnimatedAtlas, type PagedMap } from "../atlas-pages.ts";

/**
 * What of its neighbours the game page draws beside the map it stands on (ADR-147 decisions 6 to 10, P1.4-T191), as plain
 * data over the world manifest's maps: where each placed neighbour sits in the owning map's frame, how far the camera may
 * go, the rows the depth sort spans, which of a neighbour's chunks the view wants fetched and which it lets go, the atlas
 * pages a neighbour let go frees, and the key an entity of any map's stream is held under. Nothing here loads, draws or
 * allocates per call except `neighbourPlaces` and `pagesToRelease`, which run when a stage starts or a neighbour is let go.
 */

/**
 * How far beyond the edge of the view, in tiles, the page fetches a neighbour's chunks and atlas pages (ADR-147 decision 8):
 * one chunk, so loading starts 45.5 tiles from a vertical seam and 39.5 from a horizontal one.
 */
export const PRELOAD_TILES = 32;

/**
 * How far beyond the edge of the view, in tiles, a whole neighbour chunk must lie before the page lets it go (ADR-147
 * decision 9). The 16 tiles between this and `PRELOAD_TILES` are the hysteresis: a player who turns back within them
 * fetches nothing again.
 */
export const UNLOAD_TILES = 48;

/**
 * The edge of the owning map a neighbour shares, or null for a neighbour that meets it only at a corner.
 */
export type NeighbourSide = "north" | "south" | "west" | "east" | null;

/**
 * A placed neighbour in the owning map's frame (ADR-147 decision 6): its top-left corner in the owner's tiles
 * (`(place_m - place_owner)`, whole tiles, so whole pixels at any tile size) and in the owner's chunks, its size in tiles
 * and chunks, and the edge it shares.
 */
export interface NeighbourPlace {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly cx: number;
  readonly cy: number;
  readonly columns: number;
  readonly rows: number;
  readonly side: NeighbourSide;
}

/**
 * A rectangle in the owning map's tiles; `right` and `bottom` are exclusive.
 */
export interface TileRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The chunk a drop picks: the index of its neighbour in the places and of the chunk in that neighbour's row-major grid,
 * -1 for none.
 */
export interface DropPick {
  place: number;
  chunk: number;
}

/**
 * The part of a manifest map the places read beyond its pages: whether its compile wrote no chunk, and the depth of its
 * place.
 */
export interface PlacedMap extends PagedMap {
  readonly blank?: true;
  readonly place?: { readonly x: number; readonly y: number; readonly depth?: number };
}

/**
 * The edge of a `width` by `height` owner a neighbour's rectangle shares, from where it lies.
 *
 * @param width The owner's width in tiles.
 * @param height The owner's height in tiles.
 * @param x The neighbour's left edge in the owner's tiles.
 * @param y The neighbour's top edge.
 * @param nWidth The neighbour's width.
 * @param nHeight The neighbour's height.
 * @returns The side, or null for a corner.
 */
function sideOf(width: number, height: number, x: number, y: number, nWidth: number, nHeight: number): NeighbourSide {
  const acrossRows = y < height && y + nHeight > 0;
  const acrossColumns = x < width && x + nWidth > 0;
  if (acrossRows && x + nWidth === 0) {
    return "west";
  }
  if (acrossRows && x === width) {
    return "east";
  }
  if (acrossColumns && y + nHeight === 0) {
    return "north";
  }
  if (acrossColumns && y === height) {
    return "south";
  }
  return null;
}

/**
 * The placed neighbours of a map in its own frame: every map `neighbourMapIds` finds sharing an edge or a corner with it,
 * at the same depth and not blank (a blank map has no ground to draw, ADR-147 decision 22), in manifest order.
 *
 * @param maps The manifest's maps.
 * @param ownerId The map the page stands on.
 * @param chunkSize Chunk size in tiles; places are whole chunks (ADR-109).
 * @returns The places; none when the owner has no place.
 * @throws RangeError when a neighbour is placed off the owner's chunk grid.
 */
export function neighbourPlaces(maps: readonly PlacedMap[], ownerId: string, chunkSize: number): NeighbourPlace[] {
  const owner = maps.find((map) => map.id === ownerId);
  if (owner?.place === undefined) {
    return [];
  }
  const from = owner.place;
  const depth = from.depth ?? 0;
  const places: NeighbourPlace[] = [];
  for (const id of neighbourMapIds(maps, ownerId)) {
    const map = maps.find((candidate) => candidate.id === id) as PlacedMap;
    const place = map.place as NonNullable<PlacedMap["place"]>;
    if (map.blank === true || (place.depth ?? 0) !== depth) {
      continue;
    }
    const x = place.x - from.x;
    const y = place.y - from.y;
    if (x % chunkSize !== 0 || y % chunkSize !== 0) {
      throw new RangeError(`map ${id} is placed ${x},${y} tiles from ${ownerId}, off the ${chunkSize}-tile chunk grid`);
    }
    places.push({ id, x, y, width: map.width, height: map.height, cx: x / chunkSize, cy: y / chunkSize, columns: Math.ceil(map.width / chunkSize), rows: Math.ceil(map.height / chunkSize), side: sideOf(owner.width, owner.height, x, y, map.width, map.height) });
  }
  return places;
}

/**
 * The camera's bounds in the owner's tiles (ADR-147 decision 10): the owning map, reaching over each edge a placed
 * neighbour shares, open or closed, as deep as the deepest neighbour on that edge. An edge with none stops the camera as
 * it always did; a neighbour that meets the map only at a corner moves no bound.
 *
 * @param width The owner's width in tiles.
 * @param height The owner's height in tiles.
 * @param places The owner's neighbours.
 * @param out The rectangle to fill.
 * @returns `out`.
 */
export function cameraBounds(width: number, height: number, places: readonly NeighbourPlace[], out: TileRect): TileRect {
  out.left = 0;
  out.top = 0;
  out.right = width;
  out.bottom = height;
  for (const place of places) {
    if (place.side === "west") {
      out.left = Math.min(out.left, place.x);
    } else if (place.side === "east") {
      out.right = Math.max(out.right, place.x + place.width);
    } else if (place.side === "north") {
      out.top = Math.min(out.top, place.y);
    } else if (place.side === "south") {
      out.bottom = Math.max(out.bottom, place.y + place.height);
    }
  }
  return out;
}

/**
 * The rows beyond the owning map the depth sort spans: every neighbour's, corners included, so each neighbour's props and
 * row-sorted tiles take `reachSortFraction` over them while the owner's rows keep their own fractions.
 *
 * @param height The owner's height in tiles.
 * @param places The owner's neighbours.
 * @returns The reach; `{ top: 0, bottom: height }` with no neighbour north or south.
 */
export function sortReachOf(height: number, places: readonly NeighbourPlace[]): SortReach {
  let top = 0;
  let bottom = height;
  for (const place of places) {
    top = Math.min(top, place.y);
    bottom = Math.max(bottom, place.y + place.height);
  }
  return { top, bottom };
}

/**
 * The chunks of a neighbour, in its own grid, that the view grown by `PRELOAD_TILES` on every side covers (ADR-147
 * decision 8). A grown edge on the seam covers none of the neighbour.
 *
 * @param view The view in the owner's tiles.
 * @param place The neighbour.
 * @param chunkSize Chunk size in tiles.
 * @param out The range to fill.
 * @returns True when the grown view covers any of the neighbour, `out` then holding the range; false when it covers none.
 */
export function preloadRange(view: TileRect, place: NeighbourPlace, chunkSize: number, out: ChunkRangeOut): boolean {
  const left = Math.max(view.left - PRELOAD_TILES, place.x) - place.x;
  const top = Math.max(view.top - PRELOAD_TILES, place.y) - place.y;
  const right = Math.min(view.right + PRELOAD_TILES, place.x + place.width) - place.x;
  const bottom = Math.min(view.bottom + PRELOAD_TILES, place.y + place.height) - place.y;
  if (right <= left || bottom <= top) {
    return false;
  }
  out.minCx = Math.floor(left / chunkSize);
  out.minCy = Math.floor(top / chunkSize);
  out.maxCx = Math.ceil(right / chunkSize) - 1;
  out.maxCy = Math.ceil(bottom / chunkSize) - 1;
  return true;
}

/**
 * The tiles between a rectangle and a view along the axis they lie furthest apart on: 0 when they touch or overlap.
 *
 * @param view The view.
 * @param left The rectangle's left edge.
 * @param top Its top edge.
 * @param right Its exclusive right edge.
 * @param bottom Its exclusive bottom edge.
 * @returns The gap in tiles.
 */
function gapTo(view: TileRect, left: number, top: number, right: number, bottom: number): number {
  const dx = left >= view.right ? left - view.right : view.left >= right ? view.left - right : 0;
  const dy = top >= view.bottom ? top - view.bottom : view.top >= bottom ? view.top - bottom : 0;
  return dx > dy ? dx : dy;
}

/**
 * The one neighbour chunk to let go this frame (ADR-147 decision 9): of the chunks in the given state, the one lying
 * furthest from the view, provided it lies wholly more than `UNLOAD_TILES` beyond it; the earliest of equals.
 *
 * @param view The view in the owner's tiles.
 * @param places The neighbours.
 * @param states Each neighbour's chunk states, row-major over its grid, beside `places`.
 * @param state The state of the chunks that may be let go (loaded).
 * @param chunkSize Chunk size in tiles.
 * @param out The pick to fill.
 * @returns True when a chunk is picked, `out` then naming it.
 */
export function dropCandidate(view: TileRect, places: readonly NeighbourPlace[], states: readonly Uint8Array[], state: number, chunkSize: number, out: DropPick): boolean {
  let best = UNLOAD_TILES;
  out.place = -1;
  out.chunk = -1;
  for (let index = 0; index < places.length; index += 1) {
    const place = places[index] as NeighbourPlace;
    const chunks = states[index];
    if (chunks === undefined) {
      continue;
    }
    for (let chunk = 0; chunk < chunks.length; chunk += 1) {
      if (chunks[chunk] !== state) {
        continue;
      }
      const left = place.x + (chunk % place.columns) * chunkSize;
      const top = place.y + Math.floor(chunk / place.columns) * chunkSize;
      const gap = gapTo(view, left, top, Math.min(left + chunkSize, place.x + place.width), Math.min(top + chunkSize, place.y + place.height));
      if (gap > best) {
        best = gap;
        out.place = index;
        out.chunk = chunk;
      }
    }
  }
  return out.place >= 0;
}

/**
 * The atlas pages letting maps go frees: those the maps draw from that no kept map does.
 *
 * @param maps The manifest's maps.
 * @param released The maps let go.
 * @param kept The maps whose pages stay held: the owner and every neighbour still drawn.
 * @param atlas The asset manifest.
 * @returns The page indices, ascending.
 */
export function pagesToRelease(maps: readonly PagedMap[], released: readonly string[], kept: readonly string[], atlas: AnimatedAtlas): number[] {
  const held = new Set<number>();
  for (const id of kept) {
    for (const page of pagesOfMap(maps, id, atlas)) {
      held.add(page);
    }
  }
  const freed = new Set<number>();
  for (const id of released) {
    for (const page of pagesOfMap(maps, id, atlas)) {
      if (!held.has(page)) {
        freed.add(page);
      }
    }
  }
  return [...freed].sort((a, b) => a - b);
}

/**
 * The most a map index or a handle may be: both are `u16` on the wire.
 */
const MAX_U16 = 0xffff;

/**
 * The key an entity is held under on a page that holds more than one map's stream (ADR-147 decision 7): handles belong to
 * the map server that sent them, so an entity is keyed by the manifest index of its map and its handle. A number, exact in
 * a double, so a table keyed by it allocates nothing to look an entity up.
 *
 * @param mapIndex The map's index in the world manifest's `maps`.
 * @param handle The entity's handle in that map's stream.
 * @returns The key.
 * @throws RangeError when either is not a whole number from 0 to 65,535.
 * @example
 * entityKey(1, 7); // 65543
 */
export function entityKey(mapIndex: number, handle: number): number {
  if (!Number.isInteger(mapIndex) || mapIndex < 0 || mapIndex > MAX_U16 || !Number.isInteger(handle) || handle < 0 || handle > MAX_U16) {
    throw new RangeError(`map index ${mapIndex} and handle ${handle} must each be a whole number from 0 to ${MAX_U16}`);
  }
  return mapIndex * (MAX_U16 + 1) + handle;
}

/**
 * The map index of an entity key.
 *
 * @param key The key.
 * @returns The manifest index of the entity's map.
 */
export function keyMapIndex(key: number): number {
  return Math.floor(key / (MAX_U16 + 1));
}

/**
 * The handle of an entity key.
 *
 * @param key The key.
 * @returns The handle in its map's stream.
 */
export function keyHandle(key: number): number {
  return key % (MAX_U16 + 1);
}
